import React, {
	ChangeEvent,
	useState,
	useReducer,
	FormEvent,
	useEffect,
} from 'react';
import { text, unitList } from '../data';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import {
	BearingInfo,
	InstallationCalculationType,
	TimkenUnit,
} from '../../components/bearing-search/types/types';
import installationCalculationReducer, {
	initialInstallationState,
} from './reducer';
import partReducer, {
	BearingPartState,
	initialBearingPartState,
} from '../../components/bearing-search/reducers/reducer';
import {
	getPartStateFromParams,
	toggleImportantNote,
	toggleNextTools,
	updateToolParams,
} from '../utils/engineering-tools';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import InstallationForm from './installation-form/InstallationForm';
import { trackStart } from '../utils/analytics';

const InstallationCalculations: React.FC = () => {
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ isCalculated, setIsCalculated ] = useState<
		Record<InstallationCalculationType, boolean>
	>( {
		Inner: false,
		Outer: false,
	} );
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);
	const [ formState, dispatch ] = useReducer(
		installationCalculationReducer,
		initialInstallationState,
	);

	useEffect( () => {
		trackStart( 'bearingInstallationCalculations' );

		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		if ( savedPart.partNumber ) {
			partDispatch( {
				type: 'reset',
				payload: savedPart,
			} );

			setSearchInput( savedPart.marketPlaceDescription || '' );
		}
	}, [] );

	// Show / hide recommended tools and important notice
	useEffect( () => {
		const showTools = Object.values( isCalculated ).some(
			( calculated ) => calculated,
		);

		toggleNextTools( showTools );
		toggleImportantNote( showTools );

		return () => {
			toggleNextTools( false );
			toggleImportantNote( false );
		};
	}, [ isCalculated ] );

	// Pass down part number to next recommended tool
	useEffect( () => {
		updateToolParams<BearingPartState>( partState );

		return () => {
			updateToolParams();
		};
	}, [ partState.marketPlaceDescription ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value;
		setSearchInput( value );
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			partDispatch( {
				type: 'select_part',
				payload: part,
			} );

			setSearchInput( part.marketPlaceDescription || '' );
		}

		const clearButtons = document.querySelectorAll('.btn--clear-form');

		clearButtons.forEach((button) => {
			button.click();
		});

		setIsPartOpen( false );
	};

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();

		if ( ! partState.partNumber ) {
			// Search Part
			setIsPartOpen( true );
		}
	};

	const clearSearch = () => {
		setSearchInput( '' );
		setIsCalculated( {
			Inner: false,
			Outer: false,
		} );

		toggleImportantNote( false );

		partDispatch( {
			type: 'reset',
		} );

		dispatch( {
			type: 'reset',
		} );
	};

	const handleCalculationStatus = (
		type: InstallationCalculationType,
		status: boolean,
	) => {
		setIsCalculated( { ...isCalculated, [ type ]: status } );
	};

	return (
		<>
			<div>
				<form onSubmit={handleSubmit}>
					<div className="t-engineering-tools__form-fields">
						<TextInput
							id="installation_calculation_part_number"
							required={true}
							label={text.partNumber}
							verified={!! partState.partNumber}
							name="partNumber"
							value={searchInput}
							disabled={!! partState.partNumber}
							placeholder={text.partNumber}
							message={
								partState.partNumber
									? `${ text.bearingType }: ${ partState.bearingTypeLabel }`
									: text.partWildCardHint
							}
							onChange={inputPartNumber}
						/>

						{! partState.partNumber && (
							<button
								className="btn btn--primary"
								type="submit"
								disabled={! searchInput}
							>
								{text.lookup}
							</button>
						)}

						{!! partState.partNumber && (
							<>
								<SelectDropdown
									id="installation_calculation_unit"
									name="unit"
									required={true}
									label={text.displayUnits}
									value={formState.units}
									options={unitList}
									onChange={(event: ChangeEvent<HTMLSelectElement>) => {

										dispatch({
											type: 'change_unit',
											payload: event.target.value as TimkenUnit,
										});

										setIsPartOpen(true);
									}}
								/>

								<button
									className="btn btn--secondary"
									type="button"
									onClick={() => clearSearch()}
								>
									{text.clear}
								</button>
							</>
						)}
					</div>
				</form>
			</div>

			{!! partState.partNumber && (
				<>
					<InstallationForm
						unit={formState.units}
						type={'Inner'}
						bearing={partState}
						onCalculate={handleCalculationStatus}
					/>

					<div className="t-engineering-tools__divider"></div>

					<InstallationForm
						unit={formState.units}
						type={'Outer'}
						bearing={partState}
						onCalculate={handleCalculationStatus}
					/>
				</>
			)}

			{isPartOpen && !! searchInput && (
				<PartSearch
					partNumber={searchInput}
					onSelect={verifyPart}
					onClose={() => setIsPartOpen( false )}
					units={formState.units}
				/>
			)}
		</>
	);
};

export default InstallationCalculations;
